import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

import { concat, first, fromEvent, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

import { logger } from '@services/helper/logger.service';
import { ToastService } from '@services/helper/toast.service';

@Injectable({
  providedIn: 'root',
})
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly toastService = inject(ToastService);

  /** Guard so we don't trigger reload twice if multiple events fire close together. */
  private isReloading = false;

  constructor() {
    this.checkForUpdates();
  }

  public checkForUpdates(): Promise<void> | void {
    if (!this.swUpdate.isEnabled) return;

    // Periodic update check: once on app stable + every 5 minutes thereafter
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    const everyFiveMin$ = interval(5 * 60 * 1000);
    concat(appIsStable$, everyFiveMin$).subscribe(() => this.runUpdateCheck());

    // Wake-up trigger: tab returns to foreground after being hidden (suspended tabs)
    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.runUpdateCheck());

    // Silent activate + reload as soon as a new version is fully downloaded
    this.swUpdate.versionUpdates
      .pipe(filter((evt) => evt.type === 'VERSION_READY'))
      .subscribe(() => this.activateAndReload());

    // Service worker is in an unrecoverable state — only escape is a hard reload
    this.swUpdate.unrecoverable.subscribe(() => this.activateAndReload());
  }

  private async runUpdateCheck(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (err) {
      logger.error('SW update check failed:', err);
    }
  }

  private async activateAndReload(): Promise<void> {
    if (this.isReloading) return;
    this.isReloading = true;

    this.toastService.success('Updating to the latest version…', 1500);

    try {
      await this.swUpdate.activateUpdate();
    } catch (err) {
      logger.error('SW activate failed, forcing reload:', err);
    }

    // Small delay so users see the toast before the reload kicks in
    setTimeout(() => document.location.reload(), 1500);
  }
}
